import { Checkbox, Notice, Text, HStack, Stack, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import React, { useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import { useIsSellingOrRescuing } from '../../../hooks/useIsSellingOrRescuing';
import { useSendingChannels } from '../../../hooks/useSendingChannels';
import { customerItemState } from '../../../store/customer/atom';
import { firebaseCustomMessages } from '../../../store/firebaseCustomMassage/atom';
import { useOrderActions } from '../../../store/order/actions';
import { orderItemState } from '../../../store/order/atom';
import { hasOrderChannelsSelector, hasOrderItem } from '../../../store/order/selectors';
import { productItemState } from '../../../store/product/atom';

import { showChannelsCheckboxes, getOrderNameByType } from './constants';
import { OrderStepMobile } from './mobile';
import { ProveOrderOrigin } from './ProveOrderOrigin';

export const OrderStep = () => {
  const hasOrderChannels = useRecoilValue(hasOrderChannelsSelector);

  const productItem = useRecoilValue(productItemState);
  const orderItem = useRecoilValue(orderItemState);
  const customerItem = useRecoilValue(customerItemState);

  const hasOrder = useRecoilValue(hasOrderItem);

  const productType = productItem.current.type;
  const productTypeId = productItem.current.id;

  const { sendingChannelMessage } = useRecoilValue(firebaseCustomMessages);
  const isSellingOrRescuing = useIsSellingOrRescuing();

  const { onChannelSelectChange, sendingChannelsOptions, sendingChannelsSelected } =
    useSendingChannels(customerItem.current.customerId, isSellingOrRescuing);

  const { handleSelectOrderChannels, handleSelectOrderItem, setOrderInteractionChannelAudit } =
    useOrderActions();

  useEffect(() => {
    handleSelectOrderChannels(sendingChannelsSelected?.filter((item) => item));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendingChannelsSelected]);

  useEffect(() => {
    const formattedDate = Utils.Mask.DateFormat(new Date(), 'dd/MM/yyyy');
    handleSelectOrderItem({
      date: formattedDate,
      name: getOrderNameByType(productType),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);

  const orderStepDesktopAndTablet = (
    <VStack p="inline.xxs">
      {sendingChannelMessage && (
        <Stack w="full" pb="inline.xxxs">
          <Notice
            type="warning"
            backgroundColor={Utils.ColorToken.WithOpacity(
              'feedback.attention.lightest',
              'opacity.medium'
            )}
            textObjects={[
              { text: 'Atenção: ', variant: 'bold' },
              { text: sendingChannelMessage, variant: 'regular' },
            ]}
          />
        </Stack>
      )}
      <ProveOrderOrigin
        productTypeId={productTypeId}
        handleUpdateInteractionChannelAudit={setOrderInteractionChannelAudit}
      />
      <HStack alignItems="center" justifyContent={'space-between'}>
        {!!showChannelsCheckboxes && (
          <VStack
            space={Spacing['inline.quarck']}
            alignItems={'flex-start'}
            maxW={'22%'}
            w={'100%'}
            justifyContent={'center'}>
            <Text
              variant="semibold"
              fontSize={Typography['fontsize.md']}
              color={'neutral.black'}
              lineHeight={'distant'}>
              Selecione o canal de envio
            </Text>

            <Checkbox
              hasError={!hasOrderChannels}
              testId="channel-checkbox"
              options={sendingChannelsOptions}
              onChange={onChannelSelectChange}
              selectedValues={sendingChannelsSelected}
            />
          </VStack>
        )}
        <VStack space={'4px'} alignItems={'flex-start'}>
          <Text
            variant="semibold"
            fontSize={Typography['fontsize.md']}
            color={'neutral.black'}
            lineHeight={'distant'}>
            Data de Envio
          </Text>
          <Text
            variant="regular"
            fontSize={Typography['fontsize.md']}
            color={'neutral.black'}
            lineHeight={'distant'}>
            {hasOrder ? orderItem.date : '00/00/0000'}
          </Text>
        </VStack>
      </HStack>
    </VStack>
  );

  const orderStep = useBreakpointValue({
    base: (
      <OrderStepMobile
        channelOptions={sendingChannelsOptions}
        onChannelSelectChange={onChannelSelectChange}
        selectedChannels={sendingChannelsSelected}
        productTypeId={productTypeId}
        sendingChannelMessage={sendingChannelMessage}
      />
    ),
    sm: orderStepDesktopAndTablet,
  });

  return orderStep;
};
